import React from "react";
import { getUpdateWebformVenueInstruction, WebformVenueFromState } from "./components/model";
import { DetailSectionTemplate } from "components/MainSectionTemplate/DetailSectionTemplate";
import { OptionalTextAreaTranslationValueInput, RequiredTranslationValueInput, TranslationValueReadonly } from "./components/TranslationValueInnerForm";
import { RenderEditOptions } from "components/MainSectionTemplate/types";
import FormElementBehaviourDropdown, { getFormElementBehaviourLabel } from "components/dropdowns/reactselect/FormElementBehaviourDropdown";
import { YesNoRadioGroupForInPageEditing, YesNoText } from "components/ValidatedFields/YesNoRadioGroup";
import { nounToLower } from "utilities/translationUtils";
import { removeEmptyTranslationValues } from "utilities/freefieldUtils";

interface WebformVenueDetailSectionProps {
  webformId: number;
  venue: server.dto.WebformVenueFunctionGroup;
}

export const WebformVenueDetailSection = (props: WebformVenueDetailSectionProps) => {
  const [formState, setFormState] = React.useState<WebformVenueFromState>({
    webformId: props.webformId,

    header: props.venue.header,
    description: props.venue.description,

    contactDisplay: props.venue.contact.display,
    contactAddress: props.venue.contact.address,
    contactPosition: props.venue.contact.position,
    contactMobile: props.venue.contact.mobile,
    contactFacebook: props.venue.contact.facebook,
    contactNationality: props.venue.contact.nationality,
    contactDateOfBirth: props.venue.contact.birthDate,
    contactPassportNumber: props.venue.contact.passportNumber,
    contactSocialSecurityNumber: props.venue.contact.socialSecurity,
    contactAdditionalInformation: props.venue.contact.additional,

    companyAddressLine1: props.venue.company.address1,
    companyAddressLine2: props.venue.company.address2,
    companyPostalCode: props.venue.company.postalCode,
    companyCity: props.venue.company.city,
    companyPhone: props.venue.company.phone,
    companyWebsite: props.venue.company.website,
    companyFacebook: props.venue.company.facebook,
    companyTaxNumber: props.venue.company.taxNumber,
    companyBusinessNumber: props.venue.company.businessRegistrationNumber,
    companyAdditionalInformation: props.venue.company.additional,

    venueCapacity: props.venue.venue.capacity,
    venueAgeRestrictions: props.venue.venue.ageRestrictions,
    venueDoors: props.venue.venue.doors,
    venueCategory: props.venue.venue.category,
    venueStage: props.venue.venue.stage,
    venueVechicleAccess: props.venue.venue.vehicleAccess,
    venueParking: props.venue.venue.parking,
    venueGearAccess: props.venue.venue.gearAccess,
    venuePower: props.venue.venue.power,
    venueLighting: props.venue.venue.lighting,
    venueDressingRooms: props.venue.venue.dressingRooms,
    venueNoiseRestrictions: props.venue.venue.noiseRestrictions,
  });

  const onValueChanged = (key: string, value: any) => {
    if (key === "header") {
      setFormState(prevState => ({
        ...prevState,
        header: value
      }));
    } else if (key === "description") {
      setFormState(prevState => ({
        ...prevState,
        description: removeEmptyTranslationValues(value)
      }));
    } else if (key.startsWith("contact")) {
      setFormState(prevState => ({
        ...prevState,
        [key]: value
      }));
    } else if (key.startsWith("company")) {
      setFormState(prevState => ({
        ...prevState,
        [key]: value
      }));
    } else if (key.startsWith("venue")) {
      setFormState(prevState => ({
        ...prevState,
        [key]: value
      }));
    }
  }

  return <DetailSectionTemplate
    onValueChanged={onValueChanged}
    isInPageEditable
    sections={[{
      key: "venueDetails",
      title: <span style={{ fontSize: 18 }}>
        {`${site.currentEnvironment.functionGroups.venue.singular} ${nounToLower(RESX.GeneralLabels.FunctionGroup)}`}
      </span>,
      type: "collapsable",
      fields: [{
        key: "header",
        label: RESX.GeneralLabels.Title,
        render: () => <TranslationValueReadonly values={formState.header} />,
        inPageEdit: {
          render: ({ value, onChange, submit, isSaving }: RenderEditOptions<server.dto.TranslationValue[]>) => <RequiredTranslationValueInput
            submit={submit}
            onChange={onChange}
            values={value}
            isSaving={isSaving}
          />,
          fullWidth: true,
          type: "custom",
          initialValue: formState.header,
          submitChange: (header: server.dto.TranslationValue[]) => window.CoreApi.WebformConfiguration.UpdateVenueFunctionGroup({
            ...getUpdateWebformVenueInstruction(formState),
            header,
          }),
        },
      }, {
        key: "description",
        label: RESX.GeneralLabels.Description,
        isEmpty: formState.description.length === 0,
        render: () => <TranslationValueReadonly values={formState.description} />,
        inPageEdit: {
          render: ({ value, onChange, submit, isSaving }: RenderEditOptions<server.dto.TranslationValue[]>) => <OptionalTextAreaTranslationValueInput
            submit={submit}
            onChange={onChange}
            values={value}
            isSaving={isSaving}
          />,
          fullWidth: true,
          type: "custom",
          initialValue: formState.description,
          submitChange: (description: server.dto.TranslationValue[]) => window.CoreApi.WebformConfiguration.UpdateVenueFunctionGroup({
            ...getUpdateWebformVenueInstruction(formState),
            description,
          }),
        }
      }, {
        key: "companyHeader",
        hideLabel: true,
        label: RESX.Company.resxCompany,
        render: () => <h3>{RESX.Company.resxCompany}</h3>
      }, {
        key: "companyName",
        label: RESX.GeneralLabels.Name,
        render: () => <>{getFormElementBehaviourLabel("visibleMandatory")}</>
      }, {
        key: "companyAddressLine1",
        label: RESX.Address.AddressLine + " 1",
        render: () => <>{getFormElementBehaviourLabel(formState.companyAddressLine1)}</>,
        inPageEdit: {
          render: ({ value, onChange, cancel }) => <FormElementBehaviourDropdown
            options={[]}
            value={value}
            onChange={(value) => {
              onChange(value, true);
            }}
            onEsc={() => cancel()}
            config={{
              focusOnMount: true,
              openMenuOnFocus: true,
            }}
          />,
          fullWidth: true,
          type: "custom",
          initialValue: formState.companyAddressLine1,
          submitChange: async (companyAddressLine1: server.dto.FormElementBehaviors) =>
            window.CoreApi.WebformConfiguration.UpdateVenueFunctionGroup(getUpdateWebformVenueInstruction({
              ...formState,
              companyAddressLine1,
            })),
        },
      }, {
        key: "companyAddressLine2",
        label: RESX.Address.AddressLine + " 2",
        render: () => <>{getFormElementBehaviourLabel(formState.companyAddressLine2)}</>,
        inPageEdit: {
          render: ({ value, onChange, cancel }) => <FormElementBehaviourDropdown
            options={[]}
            value={value}
            onChange={(value) => {
              onChange(value, true);
            }}
            onEsc={() => cancel()}
            config={{
              focusOnMount: true,
              openMenuOnFocus: true,
            }}
          />,
          fullWidth: true,
          type: "custom",
          initialValue: formState.companyAddressLine2,
          submitChange: async (companyAddressLine2: server.dto.FormElementBehaviors) =>
            window.CoreApi.WebformConfiguration.UpdateVenueFunctionGroup(getUpdateWebformVenueInstruction({
              ...formState,
              companyAddressLine2,
            })),
        },
      }, {
        key: "companyPostalCode",
        label: RESX.Address.PostalCode,
        render: () => <>{getFormElementBehaviourLabel(formState.companyPostalCode)}</>,
        inPageEdit: {
          render: ({ value, onChange, cancel }) => <FormElementBehaviourDropdown
            options={[]}
            value={value}
            onChange={(value) => {
              onChange(value, true);
            }}
            onEsc={() => cancel()}
            config={{
              focusOnMount: true,
              openMenuOnFocus: true,
            }}
          />,
          fullWidth: true,
          type: "custom",
          initialValue: formState.companyPostalCode,
          submitChange: async (companyPostalCode: server.dto.FormElementBehaviors) =>
            window.CoreApi.WebformConfiguration.UpdateVenueFunctionGroup(getUpdateWebformVenueInstruction({
              ...formState,
              companyPostalCode,
            })),
        },
      }, {
        key: "companyCity",
        label: RESX.Address.City,
        render: () => <>{getFormElementBehaviourLabel(formState.companyCity)}</>,
        inPageEdit: {
          render: ({ value, onChange, cancel }) => <FormElementBehaviourDropdown
            options={[]}
            value={value}
            onChange={(value) => {
              onChange(value, true);
            }}
            onEsc={() => cancel()}
            config={{
              focusOnMount: true,
              openMenuOnFocus: true,
            }}
          />,
          fullWidth: true,
          type: "custom",
          initialValue: formState.companyCity,
          submitChange: async (companyCity: server.dto.FormElementBehaviors) =>
            window.CoreApi.WebformConfiguration.UpdateVenueFunctionGroup(getUpdateWebformVenueInstruction({
              ...formState,
              companyCity,
            })),
        },
      }, {
        key: "companyPhone",
        label: RESX.ContactDetails.PhoneNumber,
        render: () => <>{getFormElementBehaviourLabel(formState.companyPhone)}</>,
        inPageEdit: {
          render: ({ value, onChange, cancel }) => <FormElementBehaviourDropdown
            options={[]}
            value={value}
            onChange={(value) => {
              onChange(value, true);
            }}
            onEsc={() => cancel()}
            config={{
              focusOnMount: true,
              openMenuOnFocus: true,
            }}
          />,
          fullWidth: true,
          type: "custom",
          initialValue: formState.companyPhone,
          submitChange: async (companyPhone: server.dto.FormElementBehaviors) =>
            window.CoreApi.WebformConfiguration.UpdateVenueFunctionGroup(getUpdateWebformVenueInstruction({
              ...formState,
              companyPhone,
            })),
        },
      }, {
        key: "companyWebsite",
        label: RESX.Company.CompanyWebsite,
        render: () => <>{getFormElementBehaviourLabel(formState.companyWebsite)}</>,
        inPageEdit: {
          render: ({ value, onChange, cancel }) => <FormElementBehaviourDropdown
            options={[]}
            value={value}
            onChange={(value) => {
              onChange(value, true);
            }}
            onEsc={() => cancel()}
            config={{
              focusOnMount: true,
              openMenuOnFocus: true,
            }}
          />,
          fullWidth: true,
          type: "custom",
          initialValue: formState.companyWebsite,
          submitChange: async (companyWebsite: server.dto.FormElementBehaviors) =>
            window.CoreApi.WebformConfiguration.UpdateVenueFunctionGroup(getUpdateWebformVenueInstruction({
              ...formState,
              companyWebsite,
            })),
        },
      }, {
        key: "companyFacebook",
        label: "Facebook",
        render: () => <>{getFormElementBehaviourLabel(formState.companyFacebook)}</>,
        inPageEdit: {
          render: ({ value, onChange, cancel }) => <FormElementBehaviourDropdown
            options={[]}
            value={value}
            onChange={(value) => {
              onChange(value, true);
            }}
            onEsc={() => cancel()}
            config={{
              focusOnMount: true,
              openMenuOnFocus: true,
            }}
          />,
          fullWidth: true,
          type: "custom",
          initialValue: formState.companyFacebook,
          submitChange: async (companyFacebook: server.dto.FormElementBehaviors) =>
            window.CoreApi.WebformConfiguration.UpdateVenueFunctionGroup(getUpdateWebformVenueInstruction({
              ...formState,
              companyFacebook,
            })),
        },
      }, {
        key: "companyTaxNumber",
        label: RESX.RegistrationNumber.VatNumber,
        render: () => <>{getFormElementBehaviourLabel(formState.companyTaxNumber)}</>,
        inPageEdit: {
          render: ({ value, onChange, cancel }) => <FormElementBehaviourDropdown
            options={[]}
            value={value}
            onChange={(value) => {
              onChange(value, true);
            }}
            onEsc={() => cancel()}
            config={{
              focusOnMount: true,
              openMenuOnFocus: true,
            }}
          />,
          fullWidth: true,
          type: "custom",
          initialValue: formState.companyTaxNumber,
          submitChange: async (companyTaxNumber: server.dto.FormElementBehaviors) =>
            window.CoreApi.WebformConfiguration.UpdateVenueFunctionGroup(getUpdateWebformVenueInstruction({
              ...formState,
              companyTaxNumber,
            })),
        },
      }, {
        key: "companyBusinessNumber",
        label: RESX.RegistrationNumber.BusinessLicenceNumber,
        render: () => <>{getFormElementBehaviourLabel(formState.companyBusinessNumber)}</>,
        inPageEdit: {
          render: ({ value, onChange, cancel }) => <FormElementBehaviourDropdown
            options={[]}
            value={value}
            onChange={(value) => {
              onChange(value, true);
            }}
            onEsc={() => cancel()}
            config={{
              focusOnMount: true,
              openMenuOnFocus: true,
            }}
          />,
          fullWidth: true,
          type: "custom",
          initialValue: formState.companyBusinessNumber,
          submitChange: async (companyBusinessNumber: server.dto.FormElementBehaviors) =>
            window.CoreApi.WebformConfiguration.UpdateVenueFunctionGroup(getUpdateWebformVenueInstruction({
              ...formState,
              companyBusinessNumber,
            })),
        },
      }, {
        key: "companyAdditionalInformation",
        label: RESX.Company.AdditionalInformation,
        sublabel: RESX.Webform.FreeTextField,
        render: () => <YesNoText value={formState.companyAdditionalInformation} />,
        inPageEdit: {
          render: ({ value, onChange }: RenderEditOptions<boolean>) => <YesNoRadioGroupForInPageEditing
            value={value}
            onChange={v => onChange(v, true)}
          />,
          fullWidth: true,
          type: "custom",
          initialValue: formState.companyAdditionalInformation,
          submitChange: async (companyAdditionalInformation: boolean) =>
            window.CoreApi.WebformConfiguration.UpdateVenueFunctionGroup(getUpdateWebformVenueInstruction({
              ...formState,
              companyAdditionalInformation,
            })),
        }
      }, {
        key: "venueHeader",
        hideLabel: true,
        label: site.currentEnvironment.functionGroups.venue.singular,
        render: () => <h3>{site.currentEnvironment.functionGroups.venue.singular}</h3>
      }, {
        key: "venueCapacity",
        label: RESX.Venue.Capacity,
        render: () => <>{getFormElementBehaviourLabel(formState.venueCapacity)}</>,
        inPageEdit: {
          render: ({ value, onChange, cancel }) => <FormElementBehaviourDropdown
            options={[]}
            value={value}
            onChange={(value) => {
              onChange(value, true);
            }}
            onEsc={() => cancel()}
            config={{
              focusOnMount: true,
              openMenuOnFocus: true,
            }}
          />,
          fullWidth: true,
          type: "custom",
          initialValue: formState.venueCapacity,
          submitChange: async (venueCapacity: server.dto.FormElementBehaviors) =>
            window.CoreApi.WebformConfiguration.UpdateVenueFunctionGroup(getUpdateWebformVenueInstruction({
              ...formState,
              venueCapacity,
            })),
        },
      }, {
        key: "venueAgeRestrictions",
        label: RESX.Venue.AgeRestrictions,
        render: () => <>{getFormElementBehaviourLabel(formState.venueAgeRestrictions)}</>,
        inPageEdit: {
          render: ({ value, onChange, cancel }) => <FormElementBehaviourDropdown
            options={[]}
            value={value}
            onChange={(value) => {
              onChange(value, true);
            }}
            onEsc={() => cancel()}
            config={{
              focusOnMount: true,
              openMenuOnFocus: true,
            }}
          />,
          fullWidth: true,
          type: "custom",
          initialValue: formState.venueAgeRestrictions,
          submitChange: async (venueAgeRestrictions: server.dto.FormElementBehaviors) =>
            window.CoreApi.WebformConfiguration.UpdateVenueFunctionGroup(getUpdateWebformVenueInstruction({
              ...formState,
              venueAgeRestrictions,
            })),
        },
      }, {
        key: "venueDoors",
        label: RESX.Venue.Doors,
        render: () => <>{getFormElementBehaviourLabel(formState.venueDoors)}</>,
        inPageEdit: {
          render: ({ value, onChange, cancel }) => <FormElementBehaviourDropdown
            options={[]}
            value={value}
            onChange={(value) => {
              onChange(value, true);
            }}
            onEsc={() => cancel()}
            config={{
              focusOnMount: true,
              openMenuOnFocus: true,
            }}
          />,
          fullWidth: true,
          type: "custom",
          initialValue: formState.venueDoors,
          submitChange: async (venueDoors: server.dto.FormElementBehaviors) =>
            window.CoreApi.WebformConfiguration.UpdateVenueFunctionGroup(getUpdateWebformVenueInstruction({
              ...formState,
              venueDoors,
            })),
        },
      }, {
        key: "venueCategory",
        label: RESX.Venue.Category,
        render: () => <>{getFormElementBehaviourLabel(formState.venueCategory)}</>,
        inPageEdit: {
          render: ({ value, onChange, cancel }) => <FormElementBehaviourDropdown
            options={[]}
            value={value}
            onChange={(value) => {
              onChange(value, true);
            }}
            onEsc={() => cancel()}
            config={{
              focusOnMount: true,
              openMenuOnFocus: true,
            }}
          />,
          fullWidth: true,
          type: "custom",
          initialValue: formState.venueCategory,
          submitChange: async (venueCategory: server.dto.FormElementBehaviors) =>
            window.CoreApi.WebformConfiguration.UpdateVenueFunctionGroup(getUpdateWebformVenueInstruction({
              ...formState,
              venueCategory,
            })),
        },
      }, {
        key: "venueStage",
        label: RESX.Venue.Stage,
        render: () => <>{getFormElementBehaviourLabel(formState.venueStage)}</>,
        inPageEdit: {
          render: ({ value, onChange, cancel }) => <FormElementBehaviourDropdown
            options={[]}
            value={value}
            onChange={(value) => {
              onChange(value, true);
            }}
            onEsc={() => cancel()}
            config={{
              focusOnMount: true,
              openMenuOnFocus: true,
            }}
          />,
          fullWidth: true,
          type: "custom",
          initialValue: formState.venueStage,
          submitChange: async (venueStage: server.dto.FormElementBehaviors) =>
            window.CoreApi.WebformConfiguration.UpdateVenueFunctionGroup(getUpdateWebformVenueInstruction({
              ...formState,
              venueStage,
            })),
        },
      }, {
        key: "venueVechicleAccess",
        label: RESX.Venue.VehicleAccess,
        render: () => <>{getFormElementBehaviourLabel(formState.venueVechicleAccess)}</>,
        inPageEdit: {
          render: ({ value, onChange, cancel }) => <FormElementBehaviourDropdown
            options={[]}
            value={value}
            onChange={(value) => {
              onChange(value, true);
            }}
            onEsc={() => cancel()}
            config={{
              focusOnMount: true,
              openMenuOnFocus: true,
            }}
          />,
          fullWidth: true,
          type: "custom",
          initialValue: formState.venueVechicleAccess,
          submitChange: async (venueVechicleAccess: server.dto.FormElementBehaviors) =>
            window.CoreApi.WebformConfiguration.UpdateVenueFunctionGroup(getUpdateWebformVenueInstruction({
              ...formState,
              venueVechicleAccess,
            })),
        },
      }, {
        key: "venueParking",
        label: RESX.Venue.Parking,
        render: () => <>{getFormElementBehaviourLabel(formState.venueParking)}</>,
        inPageEdit: {
          render: ({ value, onChange, cancel }) => <FormElementBehaviourDropdown
            options={[]}
            value={value}
            onChange={(value) => {
              onChange(value, true);
            }}
            onEsc={() => cancel()}
            config={{
              focusOnMount: true,
              openMenuOnFocus: true,
            }}
          />,
          fullWidth: true,
          type: "custom",
          initialValue: formState.venueParking,
          submitChange: async (venueParking: server.dto.FormElementBehaviors) =>
            window.CoreApi.WebformConfiguration.UpdateVenueFunctionGroup(getUpdateWebformVenueInstruction({
              ...formState,
              venueParking,
            })),
        },
      }, {
        key: "venueGearAccess",
        label: RESX.Venue.GearAccess,
        render: () => <>{getFormElementBehaviourLabel(formState.venueGearAccess)}</>,
        inPageEdit: {
          render: ({ value, onChange, cancel }) => <FormElementBehaviourDropdown
            options={[]}
            value={value}
            onChange={(value) => {
              onChange(value, true);
            }}
            onEsc={() => cancel()}
            config={{
              focusOnMount: true,
              openMenuOnFocus: true,
            }}
          />,
          fullWidth: true,
          type: "custom",
          initialValue: formState.venueGearAccess,
          submitChange: async (venueGearAccess: server.dto.FormElementBehaviors) =>
            window.CoreApi.WebformConfiguration.UpdateVenueFunctionGroup(getUpdateWebformVenueInstruction({
              ...formState,
              venueGearAccess,
            })),
        },
      }, {
        key: "venuePower",
        label: RESX.Venue.Power,
        render: () => <>{getFormElementBehaviourLabel(formState.venuePower)}</>,
        inPageEdit: {
          render: ({ value, onChange, cancel }) => <FormElementBehaviourDropdown
            options={[]}
            value={value}
            onChange={(value) => {
              onChange(value, true);
            }}
            onEsc={() => cancel()}
            config={{
              focusOnMount: true,
              openMenuOnFocus: true,
            }}
          />,
          fullWidth: true,
          type: "custom",
          initialValue: formState.venuePower,
          submitChange: async (venuePower: server.dto.FormElementBehaviors) =>
            window.CoreApi.WebformConfiguration.UpdateVenueFunctionGroup(getUpdateWebformVenueInstruction({
              ...formState,
              venuePower,
            })),
        },
      }, {
        key: "venueLighting",
        label: RESX.Venue.Lighting,
        render: () => <>{getFormElementBehaviourLabel(formState.venueLighting)}</>,
        inPageEdit: {
          render: ({ value, onChange, cancel }) => <FormElementBehaviourDropdown
            options={[]}
            value={value}
            onChange={(value) => {
              onChange(value, true);
            }}
            onEsc={() => cancel()}
            config={{
              focusOnMount: true,
              openMenuOnFocus: true,
            }}
          />,
          fullWidth: true,
          type: "custom",
          initialValue: formState.venueLighting,
          submitChange: async (venueLighting: server.dto.FormElementBehaviors) =>
            window.CoreApi.WebformConfiguration.UpdateVenueFunctionGroup(getUpdateWebformVenueInstruction({
              ...formState,
              venueLighting,
            })),
        },
      }, {
        key: "venueDressingRooms",
        label: RESX.Venue.DressingRooms,
        render: () => <>{getFormElementBehaviourLabel(formState.venueDressingRooms)}</>,
        inPageEdit: {
          render: ({ value, onChange, cancel }) => <FormElementBehaviourDropdown
            options={[]}
            value={value}
            onChange={(value) => {
              onChange(value, true);
            }}
            onEsc={() => cancel()}
            config={{
              focusOnMount: true,
              openMenuOnFocus: true,
            }}
          />,
          fullWidth: true,
          type: "custom",
          initialValue: formState.venueDressingRooms,
          submitChange: async (venueDressingRooms: server.dto.FormElementBehaviors) =>
            window.CoreApi.WebformConfiguration.UpdateVenueFunctionGroup(getUpdateWebformVenueInstruction({
              ...formState,
              venueDressingRooms,
            })),
        },
      }, {
        key: "venueNoiseRestrictions",
        label: RESX.Venue.NoiseRestrictions,
        render: () => <>{getFormElementBehaviourLabel(formState.venueNoiseRestrictions)}</>,
        inPageEdit: {
          render: ({ value, onChange, cancel }) => <FormElementBehaviourDropdown
            options={[]}
            value={value}
            onChange={(value) => {
              onChange(value, true);
            }}
            onEsc={() => cancel()}
            config={{
              focusOnMount: true,
              openMenuOnFocus: true,
            }}
          />,
          fullWidth: true,
          type: "custom",
          initialValue: formState.venueNoiseRestrictions,
          submitChange: async (venueNoiseRestrictions: server.dto.FormElementBehaviors) =>
            window.CoreApi.WebformConfiguration.UpdateVenueFunctionGroup(getUpdateWebformVenueInstruction({
              ...formState,
              venueNoiseRestrictions,
            })),
        },
      }, {
        key: "contactHeader",
        hideLabel: true,
        label: RESX.Contact.resxContact,
        render: () => <h3>{RESX.Contact.resxContact}</h3>
      }, {
        key: "contactDisplay",
        label: RESX.GeneralLabels.Display,
        render: () => <YesNoText value={formState.contactDisplay} />,
        inPageEdit: {
          render: ({ value, onChange }: RenderEditOptions<boolean>) => <YesNoRadioGroupForInPageEditing
            value={value}
            onChange={v => onChange(v, true)}
          />,
          fullWidth: true,
          type: "custom",
          initialValue: formState.contactDisplay,
          submitChange: (contactDisplay: boolean) => window.CoreApi.WebformConfiguration.UpdateVenueFunctionGroup(getUpdateWebformVenueInstruction({
            ...formState,
            contactDisplay,
          })),
        }
      }, {
        key: "contactName",
        hidden: !formState.contactDisplay,
        label: RESX.GeneralLabels.Name,
        render: () => <>{getFormElementBehaviourLabel("visibleMandatory")}</>
      }, {
        key: "contactEmail",
        hidden: !formState.contactDisplay,
        label: RESX.GeneralLabels.Email,
        render: () => <>{getFormElementBehaviourLabel("visibleMandatory")}</>
      }, {
        key: "contactAddress",
        hidden: !formState.contactDisplay,
        label: RESX.Address.resxAddress,
        render: () => <>{getFormElementBehaviourLabel(formState.contactAddress)}</>,
        inPageEdit: {
          render: ({ value, onChange, cancel }) => <FormElementBehaviourDropdown
            options={[]}
            value={value}
            onChange={(value) => {
              onChange(value, true);
            }}
            onEsc={() => cancel()}
            config={{
              focusOnMount: true,
              openMenuOnFocus: true,
            }}
          />,
          fullWidth: true,
          type: "custom",
          initialValue: formState.contactAddress,
          submitChange: async (contactAddress: server.dto.FormElementBehaviors) =>
            window.CoreApi.WebformConfiguration.UpdateVenueFunctionGroup(getUpdateWebformVenueInstruction({
              ...formState,
              contactAddress,
            })),
        },
      }, {
        key: "contactPosition",
        hidden: !formState.contactDisplay,
        label: RESX.Contact.Position,
        render: () => <>{getFormElementBehaviourLabel(formState.contactPosition)}</>,
        inPageEdit: {
          render: ({ value, onChange, cancel }) => <FormElementBehaviourDropdown
            options={[]}
            value={value}
            onChange={(value) => {
              onChange(value, true);
            }}
            onEsc={() => cancel()}
            config={{
              focusOnMount: true,
              openMenuOnFocus: true,
            }}
          />,
          fullWidth: true,
          type: "custom",
          initialValue: formState.contactPosition,
          submitChange: async (contactPosition: server.dto.FormElementBehaviors) =>
            window.CoreApi.WebformConfiguration.UpdateVenueFunctionGroup(getUpdateWebformVenueInstruction({
              ...formState,
              contactPosition,
            })),
        },
      }, {
        key: "contactMobile",
        hidden: !formState.contactDisplay,
        label: RESX.Contact.Mobile,
        render: () => <>{getFormElementBehaviourLabel(formState.contactMobile)}</>,
        inPageEdit: {
          render: ({ value, onChange, cancel }) => <FormElementBehaviourDropdown
            options={[]}
            value={value}
            onChange={(value) => {
              onChange(value, true);
            }}
            onEsc={() => cancel()}
            config={{
              focusOnMount: true,
              openMenuOnFocus: true,
            }}
          />,
          fullWidth: true,
          type: "custom",
          initialValue: formState.contactMobile,
          submitChange: async (contactMobile: server.dto.FormElementBehaviors) =>
            window.CoreApi.WebformConfiguration.UpdateVenueFunctionGroup(getUpdateWebformVenueInstruction({
              ...formState,
              contactMobile,
            })),
        },
      }, {
        key: "contactFacebook",
        hidden: !formState.contactDisplay,
        label: "Facebook",
        render: () => <>{getFormElementBehaviourLabel(formState.contactFacebook)}</>,
        inPageEdit: {
          render: ({ value, onChange, cancel }) => <FormElementBehaviourDropdown
            options={[]}
            value={value}
            onChange={(value) => {
              onChange(value, true);
            }}
            onEsc={() => cancel()}
            config={{
              focusOnMount: true,
              openMenuOnFocus: true,
            }}
          />,
          fullWidth: true,
          type: "custom",
          initialValue: formState.contactFacebook,
          submitChange: async (contactFacebook: server.dto.FormElementBehaviors) =>
            window.CoreApi.WebformConfiguration.UpdateVenueFunctionGroup(getUpdateWebformVenueInstruction({
              ...formState,
              contactFacebook,
            })),
        },
      }, {
        key: "contactNationality",
        hidden: !formState.contactDisplay,
        label: RESX.Passport.Nationality,
        render: () => <>{getFormElementBehaviourLabel(formState.contactNationality)}</>,
        inPageEdit: {
          render: ({ value, onChange, cancel }) => <FormElementBehaviourDropdown
            options={[]}
            value={value}
            onChange={(value) => {
              onChange(value, true);
            }}
            onEsc={() => cancel()}
            config={{
              focusOnMount: true,
              openMenuOnFocus: true,
            }}
          />,
          fullWidth: true,
          type: "custom",
          initialValue: formState.contactNationality,
          submitChange: async (contactNationality: server.dto.FormElementBehaviors) =>
            window.CoreApi.WebformConfiguration.UpdateVenueFunctionGroup(getUpdateWebformVenueInstruction({
              ...formState,
              contactNationality,
            })),
        },
      }, {
        key: "contactDateOfBirth",
        hidden: !formState.contactDisplay,
        label: RESX.Contact.DateOfBirth,
        render: () => <>{getFormElementBehaviourLabel(formState.contactDateOfBirth)}</>,
        inPageEdit: {
          render: ({ value, onChange, cancel }) => <FormElementBehaviourDropdown
            options={[]}
            value={value}
            onChange={(value) => {
              onChange(value, true);
            }}
            onEsc={() => cancel()}
            config={{
              focusOnMount: true,
              openMenuOnFocus: true,
            }}
          />,
          fullWidth: true,
          type: "custom",
          initialValue: formState.contactDateOfBirth,
          submitChange: async (contactDateOfBirth: server.dto.FormElementBehaviors) =>
            window.CoreApi.WebformConfiguration.UpdateVenueFunctionGroup(getUpdateWebformVenueInstruction({
              ...formState,
              contactDateOfBirth,
            })),
        },
      }, {
        key: "contactPassportNumber",
        hidden: !formState.contactDisplay,
        label: RESX.Passport.PassportNumber,
        render: () => <>{getFormElementBehaviourLabel(formState.contactPassportNumber)}</>,
        inPageEdit: {
          render: ({ value, onChange, cancel }) => <FormElementBehaviourDropdown
            options={[]}
            value={value}
            onChange={(value) => {
              onChange(value, true);
            }}
            onEsc={() => cancel()}
            config={{
              focusOnMount: true,
              openMenuOnFocus: true,
            }}
          />,
          fullWidth: true,
          type: "custom",
          initialValue: formState.contactPassportNumber,
          submitChange: async (contactPassportNumber: server.dto.FormElementBehaviors) =>
            window.CoreApi.WebformConfiguration.UpdateVenueFunctionGroup(getUpdateWebformVenueInstruction({
              ...formState,
              contactPassportNumber,
            })),
        },
      }, {
        key: "contactSocialSecurityNumber",
        hidden: !formState.contactDisplay,
        label: RESX.Contact.SocialSecurity,
        render: () => <>{getFormElementBehaviourLabel(formState.contactSocialSecurityNumber)}</>,
        inPageEdit: {
          render: ({ value, onChange, cancel }) => <FormElementBehaviourDropdown
            options={[]}
            value={value}
            onChange={(value) => {
              onChange(value, true);
            }}
            onEsc={() => cancel()}
            config={{
              focusOnMount: true,
              openMenuOnFocus: true,
            }}
          />,
          fullWidth: true,
          type: "custom",
          initialValue: formState.contactSocialSecurityNumber,
          submitChange: async (contactSocialSecurityNumber: server.dto.FormElementBehaviors) =>
            window.CoreApi.WebformConfiguration.UpdateVenueFunctionGroup(getUpdateWebformVenueInstruction({
              ...formState,
              contactSocialSecurityNumber,
            })),
        },
      }, {
        key: "contactAdditionalInformation",
        hidden: !formState.contactDisplay,
        label: RESX.Company.AdditionalInformation,
        sublabel: RESX.Webform.FreeTextField,
        render: () => <YesNoText value={formState.contactAdditionalInformation} />,
        inPageEdit: {
          render: ({ value, onChange }: RenderEditOptions<boolean>) => <YesNoRadioGroupForInPageEditing
            value={value}
            onChange={v => onChange(v, true)}
          />,
          fullWidth: true,
          type: "custom",
          initialValue: formState.contactAdditionalInformation,
          submitChange: async (contactAdditionalInformation: boolean) =>
            window.CoreApi.WebformConfiguration.UpdateVenueFunctionGroup(getUpdateWebformVenueInstruction({
              ...formState,
              contactAdditionalInformation,
            })),
        }
      }]
    }]}
  />
}